import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { type components } from "@njbsoft/types/company";
import {
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  SkeletonTable,
  Tabs,
  type TabDef,
} from "@/components/ui";
import { StatusChips, emptyStatusCounts } from "@/components/submissions/status-chips";
import {
  buildSubmissionsColumns,
  columnIdToSortBy,
  COMPANY_SUBMISSIONS_COLUMN_COUNT,
} from "@/components/company/submissions/submissions-columns";
import {
  SubmissionsFilterBar,
  emptySubmissionsFilters,
  hasSubmissionsFilters,
  performedBound,
  type SubmissionsTesterOption,
  type SubmissionsUtilityOption,
} from "@/components/company/submissions/submissions-filters";
import { CompanyChangeRequestsTab } from "@/components/company/submissions/change-requests-tab";
import { useActiveMembership } from "@/hooks/use-company-context";
import { useCompanyUtilities } from "@/hooks/use-company-utilities";
import { MEMBER_PICKER_PARAMS, useCompanyMembers } from "@/hooks/use-company-members";
import {
  useCompanyTestSubmissions,
  type CompanyTestSubmissionsParams,
} from "@/hooks/use-company-test-submissions";

type SubmissionsPageTab = "submissions" | "change-requests";

interface SubmissionsSearch {
  /** Absent: the archive of submitted documents. */
  tab?: "change-requests";
}

export const Route = createFileRoute("/_company/company/submissions/")({
  validateSearch: (search: Record<string, unknown>): SubmissionsSearch => ({
    tab: search.tab === "change-requests" ? "change-requests" : undefined,
  }),
  component: CompanySubmissionsPage,
});

type CompanyTestSubmissionRow = components["schemas"]["CompanyTestSubmissionRow"];
type FormSubmissionStatus = components["schemas"]["FormSubmissionStatus"];
type FormSubmissionOutcome = components["schemas"]["FormSubmissionOutcome"];

const PAGE_SIZE = 20;
// Newest work first: an administrator opening the archive is looking for what
// was just filed far more often than for what was filed two years ago.
const DEFAULT_SORT: SortingState = [{ id: "performed_at", desc: true }];

/**
 * The Submissions page: the archive of submitted documents, and — under
 * `?tab=change-requests` — the change-request sets the members sent. The page
 * owns what both tabs filter by: the water systems whose authorization edge
 * with the company is still ACTIVE, and the live roster by PROFILE id.
 */
function CompanySubmissionsPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { tab } = Route.useSearch();
  const activeTab: SubmissionsPageTab = tab ?? "submissions";

  const active = useActiveMembership();
  const companyId = active?.company_id ?? "";
  const utilities = useCompanyUtilities(companyId);
  const membersQuery = useCompanyMembers(companyId, MEMBER_PICKER_PARAMS);

  // The utility dropdown's only admissible source: the water systems whose
  // authorization edge with the company is still ACTIVE. A paused or ended edge
  // returns no rows, so offering it would be a filter that can only empty the
  // table.
  const orgOptions = useMemo<SubmissionsUtilityOption[]>(() => {
    const seen = new Set<string>();
    const options: SubmissionsUtilityOption[] = [];
    for (const edge of utilities.rows) {
      if (edge.status !== "active" || seen.has(edge.org_id)) continue;
      seen.add(edge.org_id);
      options.push({ id: edge.org_id, name: edge.org_name });
    }
    return options;
  }, [utilities.rows]);

  // The tester dropdown's source: the live roster, addressed by PROFILE id —
  // the archive row's own `performed_by`, a set's `member_profile_id`. The
  // picker params read the roster's maximum page (100); a roster larger than
  // that leaves its newest members unlisted here, a narrowing the filter cannot
  // offer until the roster read grows one.
  const testerOptions = useMemo<SubmissionsTesterOption[]>(() => {
    const seen = new Set<string>();
    const options: SubmissionsTesterOption[] = [];
    for (const member of membersQuery.data?.items ?? []) {
      if (member.ended_at || seen.has(member.profile_id)) continue;
      seen.add(member.profile_id);
      options.push({
        profileId: member.profile_id,
        label: member.full_name ?? member.email ?? member.profile_id,
      });
    }
    return options;
  }, [membersQuery.data]);

  const tabs: TabDef<SubmissionsPageTab>[] = [
    { value: "submissions", label: t("company:submissions.title") },
    { value: "change-requests", label: t("company:nav.changeRequests") },
  ];
  const selectTab = (next: SubmissionsPageTab) => {
    void navigate({
      to: "/company/submissions",
      search: next === "submissions" ? {} : { tab: next },
      replace: true,
    });
  };

  return (
    <div className="space-y-4 p-8">
      <PageHeader
        title={t("company:submissions.title")}
        subtitle={t("company:submissions.subtitle")}
      />
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={selectTab}
        ariaLabel={t("company:submissions.title")}
      />
      <div
        role="tabpanel"
        id={`tabpanel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        className="space-y-4"
      >
        {activeTab === "submissions" ? (
          <SubmissionsTab companyId={companyId} orgs={orgOptions} testers={testerOptions} />
        ) : (
          <CompanyChangeRequestsTab
            companyId={companyId}
            orgs={orgOptions}
            testers={testerOptions}
          />
        )}
      </div>
    </div>
  );
}

interface SubmissionsTabProps {
  companyId: string;
  orgs: SubmissionsUtilityOption[];
  testers: SubmissionsTesterOption[];
}

/**
 * The company's whole archive of submitted regulatory documents — every live
 * member's work across every water system that has authorized the company,
 * with the filters, sort and pages the server does for the tester's own
 * archive. The company's record, not a per-person one.
 *
 * SERVER-PAGED throughout: the page, both sort axes and all six filters are
 * the server's work, and every one of them rides a single assembled params
 * object. Sorting only the rows currently loaded would reorder one page and
 * lie about the rest, so `DataTable` keeps its default `manualSorting` and no
 * `getSortedRowModel` is installed.
 *
 * ONE STATUS FILTER, TWO CONTROLS: the chip strip and the filter bar's status
 * Select read and write the same `filters.status` state. The strip carries the
 * per-status counts from `status_counts`, which the server computes over the
 * filtered set MINUS the status predicate — so the counts stay still while the
 * reader filters by status, answering "how is the whole archive doing" rather
 * than "how is the slice doing".
 *
 * NO SEARCH BOX: the operation has no free-text parameter, so the filter bar
 * composes the shared `FilterBar` shell with the search prop omitted — a box
 * sifting the twenty loaded rows would filter one page and lie about the rest.
 */
function SubmissionsTab({ companyId, orgs, testers }: SubmissionsTabProps) {
  const { t } = useTranslation(["company", "field-work", "common"]);
  const navigate = useNavigate();

  const [filters, setFilters] = useState(emptySubmissionsFilters);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORT);
  const [offset, setOffset] = useState(0);

  // Any filter or sort change returns the table to its first page.
  useEffect(() => {
    setOffset(0);
  }, [sorting, filters]);

  const sort = sorting[0];

  // The ONE place the wire params are assembled. Each empty string means "every"
  // and rides as `undefined`; the two date inputs become the RFC3339 instants
  // the bounds are typed as, drawn in the reader's own zone.
  const queryParams = useMemo<CompanyTestSubmissionsParams>(
    () => ({
      org_id: filters.orgId || undefined,
      // The filter bar holds its selects as free-form strings (the empty one
      // means "every"), and the option sets ARE the wire enums — the Select
      // cannot emit a value outside them — so narrowing here is what the widget
      // already guarantees, expressed to the type system.
      status: (filters.status || undefined) as FormSubmissionStatus | undefined,
      outcome: (filters.outcome || undefined) as FormSubmissionOutcome | undefined,
      performed_by: filters.performedBy || undefined,
      performed_from: filters.performedFrom
        ? performedBound(filters.performedFrom, "start")
        : undefined,
      performed_to: filters.performedTo ? performedBound(filters.performedTo, "end") : undefined,
      sort_by: sort ? columnIdToSortBy[sort.id] : undefined,
      sort_dir: sort ? (sort.desc ? "desc" : "asc") : undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [filters, sort, offset],
  );

  const listQuery = useCompanyTestSubmissions(companyId, queryParams);

  const openSubmission = useCallback(
    (row: CompanyTestSubmissionRow) => {
      void navigate({
        to: "/company/submissions/$submissionId",
        params: { submissionId: row.id },
      });
    },
    [navigate],
  );

  const columns = useMemo(() => buildSubmissionsColumns(t, openSubmission), [t, openSubmission]);

  const resetFilters = useCallback(() => {
    setFilters(emptySubmissionsFilters);
  }, []);

  // The strip's second control: the same `filters.status` the bar's Select
  // holds, so the two can never disagree about what is filtered.
  const setStatusFilter = useCallback((status: string) => {
    setFilters((current) => ({ ...current, status }));
  }, []);

  const items = listQuery.data?.items ?? [];
  // Every row the filter matched, not the page's length — the pagination
  // arithmetic is the only consumer.
  const totalCount = listQuery.data?.total_count ?? 0;

  return (
    <>
      <StatusChips
        counts={listQuery.data?.status_counts ?? emptyStatusCounts}
        status={filters.status}
        onStatusChange={setStatusFilter}
      />

      <SubmissionsFilterBar value={filters} orgs={orgs} testers={testers} onChange={setFilters} />

      {listQuery.isFetching && items.length === 0 ? (
        <SkeletonTable columns={COMPANY_SUBMISSIONS_COLUMN_COUNT} />
      ) : listQuery.isError ? (
        <p className="text-danger">{t("common:errors.somethingWentWrong")}</p>
      ) : items.length === 0 ? (
        hasSubmissionsFilters(filters) ? (
          <EmptyState
            title={t("company:submissions.emptyFiltered.title")}
            description={t("company:submissions.emptyFiltered.description")}
            actionLabel={t("company:submissions.filters.reset")}
            onAction={resetFilters}
          />
        ) : (
          <EmptyState
            title={t("company:submissions.empty.title")}
            description={t("company:submissions.empty.description")}
          />
        )
      ) : (
        <>
          <DataTable
            columns={columns}
            data={items}
            sorting={sorting}
            onSortingChange={setSorting}
            onRowClick={openSubmission}
          />
          <Pagination
            currentPage={Math.floor(offset / PAGE_SIZE) + 1}
            totalPages={Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
            totalItems={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={(page) => {
              setOffset((page - 1) * PAGE_SIZE);
            }}
          />
        </>
      )}
    </>
  );
}
